import { FC, MouseEvent } from 'react';
import { TTheme } from 'widgets/choose-background/ui/types';
import { UploadButton } from './UploadButton';
import { cn } from 'shared/lib';

type MediaSource =
  | { type: 'custom-image'; url: string }
  | { type: 'custom-video'; url: string }
  | { type: 'stock-image'; url: string; thumb: string };

interface IProps {
  theme: TTheme;
  onChange: (value: string) => void;
  customImageBackgrounds: string[];
  customVideoBackgrounds: string[];
  setCustomImageBackgrounds: (backgrounds: string[]) => void;
  setCustomVideoBackgrounds: (backgrounds: string[]) => void;
  selectedBackground: string;
}

export const UploadTab: FC<IProps> = ({
  selectedBackground,
  customImageBackgrounds,
  setCustomImageBackgrounds,
  customVideoBackgrounds,
  setCustomVideoBackgrounds,
  onChange
}) => {
  const combinedMedia: MediaSource[] = [
    ...customImageBackgrounds.map((url) => ({
      type: 'custom-image' as const,
      url: url.replace(/^image;/, '')
    })),
    ...customVideoBackgrounds.map((url) => ({
      type: 'custom-video' as const,
      url: url.replace(/^video;/, '')
    }))
  ];

  const handleSelect = (item: MediaSource) => {
    const prefix = item.type === 'custom-video' ? 'video' : 'image';
    onChange(`${prefix};${item.url}`);
  };

  const handleImageUpload = (url: string) => {
    const formatted = `image;${url}`;
    setCustomImageBackgrounds([formatted, ...customImageBackgrounds]);
    onChange(formatted);
  };

  const handleVideoUpload = (url: string) => {
    const formatted = `video;${url}`;
    setCustomVideoBackgrounds([formatted, ...customVideoBackgrounds]);
    onChange(formatted);
  };

  const handlePlayVideo = async (e: MouseEvent<HTMLVideoElement>) => {
    try {
      await e.currentTarget.play();
    } catch (error) {
      console.error('Failed to play video:', error);
    }
  };

  const handlePauseVideo = (e: MouseEvent<HTMLVideoElement>) => {
    e.currentTarget.pause();
  };

  return (
    <div className="ag-flex ag-flex-col ag-gap-y-2">
      <div className="ag-flex ag-flex-row ag-justify-between ag-items-center ag-text-sm">
        <p className="ag-font-bold">Upload Image / Video</p>
      </div>

      <div className="ag-grid ag-grid-cols-3 ag-gap-2">
        <UploadButton
          onImageUpload={handleImageUpload}
          onVideoUpload={handleVideoUpload}
          className="ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-flex ag-items-center ag-justify-center ag-bg-white hover:ag-bg-neutral-100"
        />

        {combinedMedia.map((item, index) => {
          const fullId =
            (item.type === 'custom-image' || item.type === 'stock-image'
              ? 'image'
              : 'video') +
            ';' +
            item.url;

          const isActive = fullId === selectedBackground;

          return (
            <div
              key={`${item.url}-${index}`}
              onClick={() => handleSelect(item)}
              className={cn(
                'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-overflow-hidden ag-cursor-pointer',
                isActive && 'ag-ring-2 ag-ring-purple-600'
              )}
            >
              {item.type === 'custom-video' ? (
                <video
                  src={item.url}
                  className="ag-w-full ag-h-full ag-object-cover"
                  onMouseOver={handlePlayVideo}
                  onMouseOut={handlePauseVideo}
                />
              ) : (
                <img
                  src={item.type === 'stock-image' ? item.thumb : item.url}
                  alt="media"
                  className="ag-w-full ag-h-full ag-object-cover"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
